/**
 * The customer-facing money on a quote, computed once.
 *
 * These sums were private to `QuoteHtmlPreviewDialog`, which was the only
 * surface that showed a customer a total. T&C templates now quote figures back
 * at the customer too (`{{quote.total}}`, `{{quote.gst}}`), and a terms
 * paragraph contradicting the Total row directly above it is worse than no
 * figure at all — so the arithmetic lives here and both callers read it.
 *
 * Everything is in the quote's own `priceDisplayMode` and carries the EFT
 * buffer on its positive lines (never on a discount or credit), because that is
 * what the customer is quoted and what the invoice will say. `quote.total` on the DTO is the raw marked-items sum: no bundle
 * discount, no diagnostics credit, no EFT uplift. It is not the number on the
 * quote.
 */

import type { QuoteDetail } from '@techgitter/contracts';

import { applyEft } from './quoteEftPricing';
import { optionLineSellDisplay } from './quotePricing';
import { resolveBillingGroups, groupLineTotal } from './quoteBillingGroups';

export const round2 = (n: number): number => Math.round(n * 100) / 100;

// Rounding at the display layer matches what the customer sees on each
// rendered row AND what the resulting invoice's per-line totals will
// be. Without it, the per-line cells render unrounded floats (e.g.
// 1045.45 * 1.1 = 1149.9950000000001) which then disagree with a
// summed total by fractional cents.
export const toDisplayPrice = (
  sellExGst: number,
  mode: 'ex' | 'inc',
): number => round2(mode === 'inc' ? sellExGst * 1.1 : sellExGst);

export const sumMarkedTotal = (quote: QuoteDetail, mode: 'ex' | 'inc'): number => {
  // Group-aware: each billing group contributes ONE EFT-uplifted line (the
  // summed member total), each ungrouped marked item its own — so the shown
  // lines sum to this subtotal, matching the backend's per-final-line bake.
  const groups = resolveBillingGroups(quote.items);
  const groupedIds = new Set(groups.flatMap((g) => g.items.map((i) => i.id)));
  let total = 0;
  for (const g of groups) {
    total += applyEft(
      groupLineTotal(g, mode),
      quote.includeEftFee,
      quote.eftFeeRate,
    );
  }
  for (const it of quote.items) {
    if (!it.markedForInvoice || !it.selectedOptionId || groupedIds.has(it.id)) {
      continue;
    }
    const opt = it.options.find((o) => o.id === it.selectedOptionId);
    if (opt)
      total += applyEft(
        optionLineSellDisplay(opt, mode),
        quote.includeEftFee,
        quote.eftFeeRate,
      );
  }
  return total;
};

/**
 * Repair-quote services aren't stored in `quote.items` — they live in
 * `quote.repairServices`, each holding a list of internal components
 * (labour / part / third_party / fixed_price). The customer-facing
 * price for a service is the sum of its components' `sellPrice`,
 * EXCEPT third_party components only contribute when their
 * vendorQuoteStatus is 'accepted' (other statuses are quote-comparison
 * audit lines and bill $0).
 *
 * Rounding is applied per-service (not per-component) because the
 * backend emits one invoice line per repair service on promote — that
 * line's price is `round(svc_sell_ex_gst * 1.1, 2)`, so summing rounded
 * per-service values here matches the invoice total exactly.
 */
export const sumRepairServicesTotal = (
  quote: QuoteDetail,
  mode: 'ex' | 'inc',
): number => {
  let total = 0;
  for (const svc of quote.repairServices ?? []) {
    let svcExGst = 0;
    for (const c of svc.components ?? []) {
      if (c.kind === 'third_party' && c.vendorQuoteStatus !== 'accepted') {
        continue;
      }
      svcExGst += Number.isFinite(c.sellPrice) ? c.sellPrice : 0;
    }
    // Per-service EFT uplift — the backend bakes one line per service.
    total += applyEft(
      toDisplayPrice(svcExGst, mode),
      quote.includeEftFee,
      quote.eftFeeRate,
    );
  }
  return total;
};


/** The GST rate every price on a quote is grossed up by. */
const GST_RATE = 0.1;

export interface QuoteCustomerTotals {
  /** The quote's own display mode — what `subtotal` / `total` are expressed in. */
  mode: 'ex' | 'inc';
  /** Sum of the customer-facing lines, before discounts and credits. */
  subtotal: number;
  /** Bundle discount, as a positive number (0 when none). */
  bundleAmount: number;
  /** Diagnostics-fee credit, as a positive number (0 when none). */
  creditAmount: number;
  /** What the customer pays: subtotal less the discount and the credit. */
  total: number;
  /** `total` expressed ex-GST, whatever the display mode. */
  totalExGst: number;
  /** `total` expressed inc-GST. */
  totalIncGst: number;
  /** The GST component of `totalIncGst`. */
  gst: number;
}

/**
 * The quote's ledger: subtotal, deductions, total, and the GST split.
 *
 * Repair and network-install quotes price from `repairServices`; everything
 * else from the marked items. Neither the bundle discount nor the diagnostics
 * credit is EFT-uplifted: the backend's bake skips every negative line, since
 * a credit is a fixed amount off. Mirroring that is what makes the preview
 * match the invoice.
 */
export function quoteCustomerTotals(quote: QuoteDetail): QuoteCustomerTotals {
  const mode = quote.priceDisplayMode ?? 'ex';
  const isServiceShaped =
    quote.type === 'repair' || quote.type === 'network_install';

  const subtotal = isServiceShaped
    ? sumRepairServicesTotal(quote, mode)
    : sumMarkedTotal(quote, mode);

  const bundle = quote.bundleDiscount;
  // No EFT uplift — a discount is a fixed amount off (backend parity).
  const bundleAmount =
    bundle && bundle.amount > 0 ? toDisplayPrice(bundle.amount, mode) : 0;

  const credit = quote.diagnosticReimbursement;
  // The credit is stored inc-GST per the contract, so an ex-GST quote divides
  // it back out before rounding — matching the invoice's DISC.Diags line. No
  // EFT uplift: the credit refunds exactly what was paid.
  const creditAmount = credit
    ? round2(mode === 'inc' ? credit.amount : credit.amount / (1 + GST_RATE))
    : 0;

  const total = round2(subtotal - bundleAmount - creditAmount);
  const totalExGst =
    mode === 'inc' ? round2(total / (1 + GST_RATE)) : total;
  const totalIncGst =
    mode === 'inc' ? total : round2(total * (1 + GST_RATE));

  return {
    mode,
    subtotal,
    bundleAmount,
    creditAmount,
    total,
    totalExGst,
    totalIncGst,
    gst: round2(totalIncGst - totalExGst),
  };
}
